import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Link2, Loader2, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { ALIQUOTE, fmtDate, fmtEUR, parseNum, round2 } from "@/lib/format";
import { REGIMI, isRC, VALUTE } from "@/lib/fiscale";
import { Field, SimpleSelect, btnGhost, inputCls } from "@/components/app/common";

function CambioField({ f, set }) {
  const [busy, setBusy] = useState(false);
  const fetchBce = async (valuta = f.valuta) => {
    if (valuta === "EUR") return;
    setBusy(true);
    try {
      const { data } = await api.get("/cambio", { params: { valuta, data: f.data } });
      set("cambio")(String(data.cambio).replace(".", ","));
      set("cambio_data")(data.data_cambio);
      toast.success(`Cambio BCE del ${fmtDate(data.data_cambio)}: 1 € = ${data.cambio} ${valuta}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Field label="Valuta"><SimpleSelect testId="fattura-valuta-select" value={f.valuta} onChange={(v) => { set("valuta")(v); if (v === "EUR") { set("cambio")("1"); set("cambio_data")(null); } else fetchBce(v); }} options={VALUTE.map((v) => ({ value: v, label: v }))} /></Field>
      {f.valuta !== "EUR" && (
        <Field label={`Cambio (${f.valuta} per 1 €)`} hint={f.cambio_data ? `BCE del ${fmtDate(f.cambio_data)} · modificabile` : "Inserisci o recupera il cambio BCE"}>
          <div className="flex gap-2">
            <Input data-testid="fattura-cambio-input" inputMode="decimal" value={f.cambio} onChange={(e) => set("cambio")(e.target.value)} className={inputCls} />
            <Button variant="outline" className={`${btnGhost} h-11 px-3`} onClick={() => fetchBce()} disabled={busy} data-testid="fattura-cambio-bce-button" title="Cambio BCE alla data fattura">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</Button>
          </div>
        </Field>
      )}
    </>
  );
}

export function FatturaFiscale({ f, set, fatture, impEur, id }) {
  const rc = isRC(f.regime);
  const integrata = round2(impEur * Number(f.aliquota_integrazione) / 100);
  const padri = fatture.filter((x) => !x.collegata_a && x.id !== id);
  return (
    <section className="card-soft p-5 sm:p-6 space-y-4" data-testid="fattura-fiscale">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2"><Field label="Regime IVA"><SimpleSelect testId="fattura-regime-select" value={f.regime} onChange={set("regime")} options={REGIMI.map(([value, label]) => ({ value, label }))} /></Field></div>
        <CambioField f={f} set={set} />
      </div>
      {rc && (
        <div className="rounded-2xl border border-[#8AA090]/40 bg-[#8AA090]/10 p-4 grid sm:grid-cols-4 gap-4 items-end" data-testid="integrazione-iva-box">
          <div className="sm:col-span-4 flex items-center gap-2 font-medium text-[#3F5446]"><Landmark className="h-4 w-4" />Integrazione IVA (reverse charge)</div>
          <Field label="Aliquota italiana"><SimpleSelect testId="fattura-aliquota-integrazione-select" value={String(f.aliquota_integrazione)} onChange={(v) => set("aliquota_integrazione")(Number(v))} options={ALIQUOTE.map((a) => ({ value: String(a), label: `${a}%` }))} /></Field>
          <Field label="Protocollo integrazione (facoltativo)"><Input data-testid="fattura-protocollo-input" value={f.protocollo_integrazione} placeholder="Es. RC-2026/015" onChange={(e) => set("protocollo_integrazione")(e.target.value)} className={inputCls} /></Field>
          <div className="sm:col-span-2 text-sm text-[#3F5446]">
            Imponibile {fmtEUR(impEur)} × {f.aliquota_integrazione}% = <b className="tabular" data-testid="iva-integrata-value">{fmtEUR(integrata)}</b> registrata sia come <b>IVA a debito</b> sia come <b>IVA a credito</b>: saldo IVA invariato. Il fornitore non addebita IVA; il costo dei prodotti resta l'imponibile.
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <Field label="Documento accessorio collegato a" hint="Per bolle doganali, spedizionieri, DHL: i costi segnati 'ripartisci' vanno sul costo dei prodotti della fattura scelta.">
            <SimpleSelect testId="fattura-collegata-select" value={f.collegata_a || "none"} onChange={(v) => set("collegata_a")(v === "none" ? null : v)} options={[{ value: "none", label: "Nessuno: è una fattura di acquisto" }, ...padri.map((x) => ({ value: x.id, label: `${x.numero || "s.n."} · ${x.fornitore_nome} · ${fmtDate(x.data)}` }))]} />
          </Field>
        </div>
        {(f.collegata_a || f.regime === "extra_ue" || parseNum(f.iva_importazione)) ? (
          <Field label="IVA all'importazione (€, a credito)" hint="Dalla bolla doganale"><Input data-testid="fattura-iva-importazione-input" inputMode="decimal" value={f.iva_importazione} placeholder="0,00" onChange={(e) => set("iva_importazione")(e.target.value)} className={inputCls} /></Field>
        ) : null}
      </div>
    </section>
  );
}

export function Collegati({ f }) {
  if (!f.padre && !f.collegati?.length) return null;
  return (
    <section className="card-soft p-5 sm:p-6" data-testid="fattura-collegati">
      <h3 className="font-medium mb-3 flex items-center gap-2"><Link2 className="h-4 w-4" />Documenti collegati</h3>
      {f.padre && <p className="text-sm">Documento accessorio della fattura <Link className="underline" to={`/fatture/${f.padre.id}`} data-testid="fattura-padre-link">{f.padre.numero || "s.n."} · {f.padre.fornitore_nome}</Link> del {fmtDate(f.padre.data)}.</p>}
      {f.collegati?.map((c, i) => (
        <div key={c.id} data-testid={`collegato-${i}`} className="flex flex-wrap gap-x-4 text-sm py-1.5 border-b border-[#EAE3DC] last:border-0">
          <Link className="underline font-medium" to={`/fatture/${c.id}`}>{c.numero || "s.n."} · {c.fornitore_nome}</Link>
          <span className="text-[#756B64]">{fmtDate(c.data)} · {c.stato}</span>
          <span className="tabular">Imponibile {fmtEUR(c.imponibile_eur)}</span>
          <span className="tabular text-[#4E6656]">ripartito sui prodotti {fmtEUR(c.ripartito_eur)}</span>
          {c.iva_importazione > 0 && <span className="tabular">IVA import. {fmtEUR(c.iva_importazione)}</span>}
        </div>
      ))}
      {f.costi_ripartiti_eur > 0 && <p className="text-sm mt-3 text-[#4E6656]" data-testid="costi-ripartiti">Costi accessori ripartiti sui prodotti di questa fattura: <b className="tabular">{fmtEUR(f.costi_ripartiti_eur)}</b> (proporzionalmente al valore delle righe).</p>}
    </section>
  );
}
